/** Desktop filter rail and shared controlled filter surface. */

import { PanelLeftClose, PanelLeftOpen, RotateCcw, SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import type { ScreenerFilterState, ScreenerState } from "../lib/types";
import { cn } from "../lib/utils";
import { AdvancedFiltersDisclosure } from "./filters/advanced-filters-disclosure";
import { FILTER_CONTROL_CONFIG } from "./filters/filter-control-config";
import { PrimaryFilterControls } from "./filters/primary-filter-controls";
import { Button } from "./ui/button";

export { FILTER_CONTROL_CONFIG };

export interface FilterControlsProps {
  state: ScreenerState;
  exchangeOptions: string[];
  onFilterPatch: (filters: Partial<ScreenerFilterState>) => void;
  onReset: () => void;
  advancedPresentation?: "popover" | "inline";
}

/** Renders primary filters plus one progressive advanced disclosure. */
export function FilterControls({
  state,
  exchangeOptions,
  onFilterPatch,
  onReset,
  advancedPresentation = "popover"
}: FilterControlsProps) {
  const filters = state.filters;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
          Filters
        </div>
        <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={onReset}>
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Reset
        </Button>
      </div>

      <PrimaryFilterControls filters={filters} onFilterPatch={onFilterPatch} />
      <AdvancedFiltersDisclosure
        filters={filters}
        exchangeOptions={exchangeOptions}
        presentation={advancedPresentation}
        onFilterPatch={onFilterPatch}
      />

      <p className="border-t border-border pt-3 text-xs leading-4 text-muted-foreground">
        Scores explain why an exported filing surfaced. They are research screening signals, not
        recommendations.
      </p>
    </div>
  );
}

/** Renders the collapsible desktop rail around the shared filter controls. */
export function FilterPanel(props: FilterControlsProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      aria-label="Screener filters"
      className={cn(
        "hidden h-full shrink-0 overflow-y-auto border-r border-border bg-surface transition-[width] duration-150 lg:block",
        collapsed ? "w-10" : "w-[264px] xl:w-[288px]"
      )}
    >
      <div className={cn("flex p-1", collapsed ? "justify-center" : "justify-end")}>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={collapsed ? "Expand filters" : "Collapse filters"}
          className="h-7 w-7"
          onClick={() => setCollapsed((current) => !current)}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <PanelLeftClose className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </Button>
      </div>
      {collapsed ? null : (
        <div className="px-2 pb-2">
          <FilterControls {...props} />
        </div>
      )}
    </aside>
  );
}
